import { X, CheckCircle2, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface Step {
  id: number;
  title: string;
  icon: any;
  color: string;
  bgGradient: string;
  details: {
    title: string;
    content: string[];
    keyPoints: string[];
    governance?: string;
    important?: string;
  };
}

interface MethodologyStepModalProps {
  isOpen: boolean;
  onClose: () => void;
  step: Step;
}

export default function MethodologyStepModal({ isOpen, onClose, step }: MethodologyStepModalProps) {
  if (!isOpen || !step) return null;

  const Icon = step.icon;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
          >
            {/* Header avec gradient */}
            <div className={`bg-gradient-to-r ${step.color} text-white p-8 relative`}>
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-white" />
              </button>
              
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <Icon className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-white/80 mb-1">ÉTAPE {step.id} / 5</div>
                  <h2 className="text-3xl font-bold mb-2">{step.details.title}</h2>
                  <p className="text-white/90 text-lg">{step.title}</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-8 space-y-6 flex-1 overflow-y-auto">
              {/* Introduction */}
              <div className="space-y-3">
                {step.details.content.map((paragraph, index) => (
                  <p key={index} className="text-gray-700 leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>

              {/* Gouvernance (si applicable) */}
              {step.details.governance && (
                <div className={`bg-gradient-to-r ${step.bgGradient} p-4 rounded-lg border-l-4 border-blue-500`}>
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-1">Gouvernance</h4>
                      <p className="text-blue-800 text-sm">{step.details.governance}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Points clés */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                  Points Clés
                </h3>
                <ul className="space-y-3">
                  {step.details.keyPoints.map((point, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-emerald-600 font-bold text-sm">{index + 1}</span>
                      </div>
                      <p className="text-gray-700 leading-relaxed flex-1">{point}</p>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Important */}
              {step.details.important && (
                <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-amber-900 mb-1">Important</h4>
                      <p className="text-amber-800 text-sm leading-relaxed">{step.details.important}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-8 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Étape {step.id} sur 5 de la méthodologie PRIsME
              </div>
              <button
                onClick={onClose}
                className="px-6 py-2 bg-bz-blue text-white rounded-lg font-semibold hover:bg-bz-dark transition-colors"
              >
                Compris
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

